import {
  KapiNotification as ProtoKapiNotification,
  KapiNotification_Level,
  KapiNotification_TrainInfo,
  KapiNotification_TrainInfo_TypeOfTimeInfo,
} from "@opstel/gen/knop/ui/v1/model/kapi_notification_pb";
import { z } from "zod/v3";
import { createSchema, ZodOutputFor } from "../zod-utils";
import { PlainMessage, Timestamp } from "@bufbuild/protobuf";
import { DateTime } from "luxon";

export type Level = "Information" | "Warning" | "Error";

export interface TrainInfo {
  trainNumber: string;
  positionInTrain: number;
  numberOfUnitsInTrain: number;
  timeInfo: DateTime;
}

export interface KapiNotification {
  message: string;
  level: Level;
  trainInfo?: TrainInfo;
}

export const trainInfoSchema = createSchema<TrainInfo, PlainMessage<KapiNotification_TrainInfo>>({
  trainNumber: z.string(),
  positionInTrain: z.number(),
  numberOfUnitsInTrain: z.number(),
  timeInfo: z
    .instanceof(Timestamp, { message: "startTime should not be empty" })
    .transform((val) => DateTime.fromJSDate(val.toDate())),
  timeInfoType: z.nativeEnum(KapiNotification_TrainInfo_TypeOfTimeInfo), // not currently used. If used, transform to string union
}) satisfies ZodOutputFor<TrainInfo>;

type ValidatedLevel = Exclude<KapiNotification_Level, KapiNotification_Level.UNKNOWN>;
function transformLevelEnum(level: ValidatedLevel): Level {
  const mapping: { [key in ValidatedLevel]: Level } = {
    [KapiNotification_Level.INFORMATION]: "Information",
    [KapiNotification_Level.WARNING]: "Warning",
    [KapiNotification_Level.ERROR]: "Error",
  };
  return mapping[level];
}
export const kapiNotificationSchema = createSchema<
  KapiNotification,
  PlainMessage<ProtoKapiNotification>
>({
  message: z.string(),
  level: z
    .nativeEnum(KapiNotification_Level)
    .refine((val) => val !== KapiNotification_Level.UNKNOWN, {
      message: "Notification level should not be unknown",
    })
    .transform(transformLevelEnum),
  trainInfo: trainInfoSchema.optional(),
}) satisfies ZodOutputFor<KapiNotification>;

export function parseKapiNotifications(
  proto: PlainMessage<ProtoKapiNotification>,
): KapiNotification {
  return kapiNotificationSchema.parse(proto);
}
